/**
 * Manage Credits Page — Add credits and view history for a client
 */

import { useState, useEffect, FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '../../services/firebase';
import { PortalHeader } from '../Layout/PortalHeader';

interface Transaction {
  id: string;
  type: 'credit' | 'debit';
  amount: number;
  balanceAfter: number;
  reason: string;
  timestamp: { toDate: () => Date };
}

export function ManageCredits() {
  const { clientId } = useParams<{ clientId: string }>();
  const [clientName, setClientName] = useState('');
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;
    const unsubscribe = onSnapshot(doc(db, 'clients', clientId), snap => {
      if (snap.exists()) {
        setClientName(snap.data().name || clientId);
        setBalance(snap.data().credits || 0);
      }
    });
    return () => unsubscribe();
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    const fetchHistory = async () => {
      const fn = httpsCallable(functions, 'getCreditHistory');
      const res = await fn({ clientId });
      const data = res.data as { transactions: Transaction[] };
      setTransactions(data.transactions);
    };
    fetchHistory();
  }, [clientId, result]);

  const handleAddCredits = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId || amount <= 0) return;
    setLoading(true);
    setResult(null);

    try {
      const fn = httpsCallable(functions, 'adminAddCredits');
      const res = await fn({ clientId, amount, reason: reason || 'Manual addition' });
      const data = res.data as { message: string };
      setResult(data.message);
      setAmount(0);
      setReason('');
    } catch (err) {
      setResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PortalHeader showCredits={false} />

      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', paddingBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to="/admin" className="back-link">Back</Link>
          <h1 style={{ margin: 0, fontSize: '1.2rem' }}>Credits: {clientName}</h1>
        </div>
        <div className="credit-badge">Balance: {balance}</div>
      </div>

      <div className="container">
        {/* Add Credits Form */}
        <h2 className="section-title">Add Credits</h2>
        <form onSubmit={handleAddCredits} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '32px', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '120px' }}>
            <label>Amount</label>
            <input type="number" value={amount || ''} onChange={e => setAmount(parseInt(e.target.value) || 0)} min="1" placeholder="100" />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: '200px' }}>
            <label>Reason</label>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="Invoice #1234 paid" />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading || amount <= 0}>
            {loading ? 'Adding...' : 'Add Credits'}
          </button>
        </form>

        {result && (
          <div style={{
            marginBottom: '24px', padding: '12px', borderRadius: '8px',
            background: result.startsWith('Error') ? 'var(--red-bg)' : 'var(--green-bg)',
            color: result.startsWith('Error') ? 'var(--red)' : 'var(--green)',
          }}>
            {result}
          </div>
        )}

        {/* Transaction History */}
        <h2 className="section-title">Transaction History</h2>
        {transactions.length === 0 ? (
          <div className="loading">No transactions yet</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Balance After</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(txn => (
                  <tr key={txn.id}>
                    <td>{txn.timestamp?.toDate?.()?.toLocaleString?.() || ''}</td>
                    <td style={{ color: txn.type === 'credit' ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                      {txn.type === 'credit' ? 'CREDIT' : 'DEBIT'}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {txn.type === 'credit' ? '+' : '-'}{txn.amount}
                    </td>
                    <td>{txn.balanceAfter}</td>
                    <td style={{ fontSize: '0.85rem' }}>{txn.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
